<template>
  <v-dialog v-model="show" max-width="500" persistent>
    <v-card>
      <v-card-title>{{ $t("finance_request_refund_title") }}</v-card-title>
      <v-divider />
      <v-card-text class="pt-4">
        <v-form ref="form" v-model="valid">
          <text-field
            v-model.number="form.orderId"
            :label="$t('finance_order_id_label')"
            type="number"
            prependInnerIcon="mdi-pound"
            :rules="[rules.required, rules.positive]"
          />
          <text-field
            v-model="form.orderRef"
            :label="$t('finance_order_description_optional')"
            prependInnerIcon="mdi-text-box-outline"
          />
          <text-field
            v-model="form.customer"
            :label="$t('finance_customer')"
            kind="user"
            :rules="[rules.required]"
          />
          <text-field
            v-model="form.reason"
            :label="$t('finance_reason')"
            prependInnerIcon="mdi-message-alert-outline"
            :rules="[rules.required]"
          />
          <text-field
            v-model.number="form.amount"
            :label="$t('finance_amount_currency', { currency: restaurantCurrency })"
            type="number"
            :suffix="restaurantCurrency"
            :hint="maxRefund != null ? $t('finance_max_refund_hint', { amount: maxRefund }) : ''"
            :persistent-hint="maxRefund != null"
            :rules="[rules.required, rules.positive, rules.maxRefund]"
          />
          <payment-method-dropdown
            v-model="form.method"
            :label="$t('finance_refund_method_optional')"
            clearable
          />
          <text-area
            v-model="form.comment"
            :label="$t('finance_comment_optional')"
            rows="2"
          />
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="saving" @click="close">{{ $t("finance_cancel") }}</v-btn>
        <primary-button :loading="saving" @click="submit">{{ $t("finance_save") }}</primary-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import PaymentMethodDropdown from "../fields/PaymentMethodDropdown.vue";

const emptyForm = () => ({
  orderId: null,
  orderRef: "",
  customer: "",
  reason: "",
  amount: 0,
  method: "",
  comment: "",
});

export default {
  name: "RefundFormDialog",

  components: { PaymentMethodDropdown },

  props: {
    value: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    // Predpopunjen ID narudžbe (npr. iz Transakcija) - kupac, vrijednost i max_refund
    // se povlače iz GET /orders/{id}/summary.
    presetOrderId: { type: [Number, String], default: null },
  },

  data() {
    return {
      valid: true,
      form: emptyForm(),
      maxRefund: null,
      summaryTimeout: null,
      rules: {
        maxRefund: (v) =>
          this.maxRefund == null ||
          Number(v) <= this.maxRefund ||
          this.$t("finance_amount_exceeds_max_refund", { amount: this.maxRefund }),
        required: (v) => (v !== null && v !== undefined && v !== "") || this.$t("finance_required_field"),
        positive: (v) => Number(v) > 0 || this.$t("finance_amount_must_be_positive"),
      },
    };
  },

  computed: {
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },

  watch: {
    value(isOpen) {
      if (!isOpen) return;
      this.form = emptyForm();
      this.maxRefund = null;
      if (this.presetOrderId) this.form.orderId = Number(this.presetOrderId);
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
      if (this.presetOrderId) this.fetchSummary();
    },
    // Ručni unos ID-a: povuci sažetak kad korisnik prestane da kuca.
    "form.orderId"(id) {
      if (!this.value || !id || Number(id) === Number(this.presetOrderId)) return;
      clearTimeout(this.summaryTimeout);
      this.summaryTimeout = setTimeout(() => this.fetchSummary(), 500);
    },
  },

  beforeDestroy() {
    clearTimeout(this.summaryTimeout);
  },

  methods: {
    // Best-effort: ako endpoint ne postoji ili narudžba nije nađena, forma ostaje ručna.
    async fetchSummary() {
      const id = Number(this.form.orderId);
      if (!id) return;
      try {
        const { data } = await restaurantFinanceService.getOrderSummary(id);
        const summary = data?.data || data || {};
        const customer = summary.customer_name || summary.customer;
        if (customer && !this.form.customer) {
          this.form.customer = typeof customer === "object" ? customer.name : customer;
        }
        this.maxRefund = summary.max_refund != null ? Number(summary.max_refund) : null;
        if (this.maxRefund != null && !Number(this.form.amount)) this.form.amount = this.maxRefund;
      } catch (error) {
        this.maxRefund = null;
      }
    },
    close() {
      this.$emit("input", false);
    },
    submit() {
      if (!this.$refs.form.validate()) return;
      this.$emit("save", {
        // order_id (backend odgovor 16.09.2026 §3.3) - mora postojati u
        // orders_guest, sad je stvarni identifikator (ranije se ID vadio
        // regexom iz order_ref, davalo pogrešne vrijednosti). order_ref
        // ostaje kao slobodan tekstualni opis, više nije obavezan.
        order_id: Number(this.form.orderId),
        order_ref: this.form.orderRef || undefined,
        customer: this.form.customer,
        reason: this.form.reason,
        amount: Number(this.form.amount),
        method: this.form.method || undefined,
        comment: this.form.comment || undefined,
      });
    },
  },
};
</script>
